import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Collapse,
  Typography,
  IconButton,
  TextField,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Divider,
  LinearProgress,
  InputAdornment,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  FirstPage as FirstPageIcon,
  LastPage as LastPageIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  BookmarkBorder as BookmarkIcon,
  Bookmark as BookmarkedIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  KeyboardArrowUp as UpIcon,
  KeyboardArrowDown as DownIcon,
  Close as CloseIcon
} from '@mui/icons-material';

const PageNavigator = ({
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  documentType = 'text',
  text = '',
  onSearch,
  searchResults = [],
  currentSearchIndex = -1
}) => {
  const [jumpToPage, setJumpToPage] = useState('');
  const [bookmarks, setBookmarks] = useState(new Set());
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // Search functionality
  const [searchQuery, setSearchQuery] = useState('');
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [wholeWords, setWholeWords] = useState(false);
  const [searchHistory, setSearchHistory] = useState([]);
  const [showSearchOptions, setShowSearchOptions] = useState(false);
  const onSearchRef = useRef(onSearch);
  
  // Update the ref when onSearch changes
  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  const handlePageJump = () => {
    const pageNum = parseInt(jumpToPage);
    if (pageNum >= 1 && pageNum <= totalPages) {
      onPageChange && onPageChange(pageNum);
      setJumpToPage('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handlePageJump();
    }
  };

  const performSearch = useCallback((query) => {
    if (!query.trim() || !text) return;

    const results = [];
    let searchText = text;
    let searchPattern = query;

    // Apply case sensitivity
    if (!caseSensitive) {
      searchText = text.toLowerCase();
      searchPattern = query.toLowerCase();
    }

    // Apply whole words option
    if (wholeWords) {
      const regex = new RegExp(`\\b${escapeRegExp(searchPattern)}\\b`, caseSensitive ? 'g' : 'gi');
      let match;
      while ((match = regex.exec(text)) !== null) {
        results.push({
          index: match.index,
          text: match[0],
          context: getContext(text, match.index, match[0].length)
        });
      }
    } else {
      let startIndex = 0;
      let index;
      while ((index = searchText.indexOf(searchPattern, startIndex)) !== -1) {
        results.push({
          index,
          text: text.substr(index, searchPattern.length),
          context: getContext(text, index, searchPattern.length)
        });
        startIndex = index + 1;
      }
    }

    onSearchRef.current && onSearchRef.current(query, results);

    // Add to search history
    if (results.length > 0) {
      setSearchHistory(prev => {
        const newHistory = [query, ...prev.filter(item => item !== query)].slice(0, 5);
        return newHistory;
      });
    }
  }, [text, caseSensitive, wholeWords]);

  // Search functionality useEffect - placed after performSearch definition
  useEffect(() => {
    if (searchQuery.trim()) {
      const timer = setTimeout(() => {
        performSearch(searchQuery);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      onSearchRef.current && onSearchRef.current('', []);
    }
  }, [searchQuery, caseSensitive, wholeWords, performSearch]);

  const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const getContext = (text, index, length) => {
    const contextLength = 50;
    const start = Math.max(0, index - contextLength);
    const end = Math.min(text.length, index + length + contextLength);
    
    let context = text.substring(start, end);
    if (start > 0) context = '...' + context;
    if (end < text.length) context = context + '...';
    
    return context;
  };

  const handleSearchHistoryClick = (historyItem) => {
    setSearchQuery(historyItem);
  };

  const clearSearch = () => {
    setSearchQuery('');
    onSearchRef.current && onSearchRef.current('', []);
  };

  const navigateResults = (direction) => {
    if (searchResults.length === 0) return;
    
    let newIndex;
    if (direction === 'next') {
      newIndex = currentSearchIndex >= searchResults.length - 1 ? 0 : currentSearchIndex + 1;
    } else {
      newIndex = currentSearchIndex <= 0 ? searchResults.length - 1 : currentSearchIndex - 1;
    }
    
    onSearchRef.current && onSearchRef.current(searchQuery, searchResults, newIndex);
  };

  const toggleBookmark = (page) => {
    const newBookmarks = new Set(bookmarks);
    if (newBookmarks.has(page)) {
      newBookmarks.delete(page);
    } else {
      newBookmarks.add(page);
    }
    setBookmarks(newBookmarks);
  };

  const navigateToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      onPageChange && onPageChange(page);
    }
  };

  const getPageSections = () => {
    // For text documents, we could potentially detect sections/headings
    // For now, we'll create logical sections based on page ranges
    const sections = [];
    const pagesPerSection = Math.max(1, Math.floor(totalPages / 10));
    
    for (let i = 1; i <= totalPages; i += pagesPerSection) {
      const endPage = Math.min(i + pagesPerSection - 1, totalPages);
      sections.push({
        name: `Pages ${i}-${endPage}`,
        startPage: i,
        endPage: endPage,
        pageCount: endPage - i + 1
      });
    }
    
    return sections;
  };

  const progress = totalPages > 0 ? (currentPage / totalPages) * 100 : 0;

  return (
    <Box sx={{ p: 2 }}>
      {/* Current Page Info */}
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Page {currentPage} of {totalPages}
        </Typography>
        <LinearProgress 
          variant="determinate" 
          value={progress} 
          sx={{ mb: 2, height: 8, borderRadius: 4 }}
        />
        <Typography variant="body2" color="text.secondary">
          {Math.round(progress)}% complete
        </Typography>
      </Box>

      {/* Navigation Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 3 }}>
        <IconButton 
          onClick={() => navigateToPage(1)}
          disabled={currentPage === 1}
          size="small"
        >
          <FirstPageIcon />
        </IconButton>
        
        <IconButton 
          onClick={() => navigateToPage(currentPage - 1)}
          disabled={currentPage === 1}
          size="small"
        >
          <PrevIcon />
        </IconButton>
        
        <IconButton 
          onClick={() => navigateToPage(currentPage + 1)}
          disabled={currentPage === totalPages}
          size="small"
        >
          <NextIcon />
        </IconButton>
        
        <IconButton 
          onClick={() => navigateToPage(totalPages)}
          disabled={currentPage === totalPages}
          size="small"
        >
          <LastPageIcon />
        </IconButton>
      </Box>

      <Button
        size="small"
        variant="text"
        onClick={() => setShowAdvanced(prev => !prev)}
        sx={{ mb: 1, textTransform: 'none' }}
      >
        {showAdvanced ? 'Hide advanced' : 'Advanced'}
      </Button>

      <Collapse in={showAdvanced}>
        {/* Search Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Search Document
          </Typography>
          
          {/* Search Input */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search in document..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={clearSearch}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{ mb: 1 }}
          />

          {/* Search Options Toggle */}
          <Button
            size="small"
            onClick={() => setShowSearchOptions(!showSearchOptions)}
            sx={{ mb: 1, textTransform: 'none' }}
          >
            {showSearchOptions ? 'Hide' : 'Show'} Options
          </Button>

          {/* Search Options */}
          {showSearchOptions && (
            <Box sx={{ mb: 2, pl: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={caseSensitive}
                    onChange={(e) => setCaseSensitive(e.target.checked)}
                  />
                }
                label="Case sensitive"
                sx={{ display: 'block', mb: 0.5 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={wholeWords}
                    onChange={(e) => setWholeWords(e.target.checked)}
                  />
                }
                label="Whole words"
                sx={{ display: 'block' }}
              />
            </Box>
          )}

          {/* Search Results */}
          {searchQuery && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
                  {searchResults.length > 0 && currentSearchIndex >= 0 && 
                    ` (${currentSearchIndex + 1}/${searchResults.length})`
                  }
                </Typography>
                
                {searchResults.length > 0 && (
                  <Box>
                    <IconButton size="small" onClick={() => navigateResults('prev')}>
                      <UpIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => navigateResults('next')}>
                      <DownIcon />
                    </IconButton>
                  </Box>
                )}
              </Box>

              {searchResults.length > 0 && (
                <Typography variant="body2" color="success.main">
                  ✓ Found in document
                </Typography>
              )}
            </Box>
          )}

          {/* Search History */}
          {searchHistory.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Recent Searches
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {searchHistory.map((item, index) => (
                  <Chip
                    key={index}
                    label={item}
                    size="small"
                    variant="outlined"
                    onClick={() => handleSearchHistoryClick(item)}
                    onDelete={() => {
                      setSearchHistory(prev => prev.filter((_, i) => i !== index));
                    }}
                    deleteIcon={<CloseIcon />}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Jump to Page */}
        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
          <TextField
            size="small"
            placeholder="Page #"
            value={jumpToPage}
            onChange={(e) => setJumpToPage(e.target.value)}
            onKeyPress={handleKeyPress}
            type="number"
            inputProps={{ min: 1, max: totalPages }}
            sx={{ flex: 1 }}
          />
          <Button 
            variant="outlined" 
            onClick={handlePageJump}
            disabled={!jumpToPage || parseInt(jumpToPage) < 1 || parseInt(jumpToPage) > totalPages}
          >
            Go
          </Button>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <IconButton 
            onClick={() => toggleBookmark(currentPage)}
            size="small"
            color={bookmarks.has(currentPage) ? 'primary' : 'default'}
          >
            {bookmarks.has(currentPage) ? <BookmarkedIcon /> : <BookmarkIcon />}
          </IconButton>
        </Box>

      {/* Bookmarks */}
      {bookmarks.size > 0 && (
        <>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Bookmarks
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
            {Array.from(bookmarks).sort((a, b) => a - b).map(page => (
              <Chip
                key={page}
                label={`Page ${page}`}
                size="small"
                variant="outlined"
                color="primary"
                onClick={() => navigateToPage(page)}
                onDelete={() => toggleBookmark(page)}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
        </>
      )}

      {/* Document Sections */}
      {totalPages > 10 && (
        <>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Quick Navigation
          </Typography>
          <List dense>
            {getPageSections().map((section, index) => (
              <ListItem key={index} disablePadding>
                <ListItemButton 
                  onClick={() => navigateToPage(section.startPage)}
                  selected={currentPage >= section.startPage && currentPage <= section.endPage}
                >
                  <ListItemText 
                    primary={section.name}
                    secondary={`${section.pageCount} page${section.pageCount !== 1 ? 's' : ''}`}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      )}

      {/* Quick Actions */}
      <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
        <Button 
          variant="outlined" 
          size="small" 
          onClick={() => navigateToPage(Math.ceil(totalPages * 0.25))}
          sx={{ flex: 1 }}
        >
          25%
        </Button>
        <Button 
          variant="outlined" 
          size="small" 
          onClick={() => navigateToPage(Math.ceil(totalPages * 0.5))}
          sx={{ flex: 1 }}
        >
          50%
        </Button>
        <Button 
          variant="outlined" 
          size="small" 
          onClick={() => navigateToPage(Math.ceil(totalPages * 0.75))}
          sx={{ flex: 1 }}
        >
          75%
        </Button>
      </Box>

      {/* Keyboard Shortcuts Help */}
      <Box sx={{ mt: 2, p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary">
          <strong>Shortcuts:</strong> Ctrl+F search, ← → arrow keys, Page Up/Down, Home/End
        </Typography>
      </Box>
      </Collapse>
    </Box>
  );
};

export default PageNavigator;
